import {
  CertificationOut,
  EPDConformityOut,
  InternalNoEPDSProductSearchOutput,
  ProductSearchInput,
  PublicationStatus,
  ReferenceUnitChoices,
  SearchEngineCategoryOUT,
  SearchMode,
  SimpleMaterialOut,
  SingleItem,
  productSearchApi,
  productsAndComponentsApi,
} from "api-client";
import { makeAutoObservable, runInAction } from "mobx";
import { DropdownOption, SelectedItems } from "./components/FiltersMenu";
import {
  OTHER_PROPERTIES_OPTIONS,
  PRODUCT_PUBLICATION_STATUS,
  REFERENCE_UNITS,
} from "features/MappingEditor/consts";
import { buildingStore } from "store/Building/BuildingStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { conformityStore } from "store/ConformityStore";

const SEARCH_PAGE_SIZE = 100;

export interface OKBCategoryType extends DropdownOption {
  parent_category?: { name: string; name_de?: string };
  filter: string;
}

export interface FiltersOptions {
  DIN_categories_level_1?: DropdownOption[];
  DIN_categories_level_2?: DropdownOption[];
  DIN_categories_level_3?: DropdownOption[];
  materials?: SimpleMaterialOut[];
  conformities?: EPDConformityOut[];
  certifications?: CertificationOut[];
  reference_units?: DropdownOption[];
  publication_status?: DropdownOption[];
  other_properties?: DropdownOption[];
}

interface SearchResults {
  count: number;
  items: InternalNoEPDSProductSearchOutput[];
  total: number;
}

export interface RemoveStateProps {
  open: boolean;
  id?: string | null;
  name?: string | null;
  type?: string | null;
}

class ProductSearchStore {
  filtersOptions: FiltersOptions = {
    DIN_categories_level_1: [],
    DIN_categories_level_2: [],
    DIN_categories_level_3: [],
    materials: [],
    conformities: [],
    certifications: [],
    reference_units: REFERENCE_UNITS,
    publication_status: PRODUCT_PUBLICATION_STATUS,
    other_properties: OTHER_PROPERTIES_OPTIONS,
  };
  selectedFilters: SelectedItems[] = [];
  searchResults: SearchResults = {
    count: 0,
    items: [],
    total: 0,
  };
  usedProducts: SearchResults = {
    count: 0,
    items: [],
    total: 0,
  };
  suggestedProducts: SearchResults = {
    count: 0,
    items: [],
    total: 0,
  };
  productSearchLoading = true;
  currentSearchPage = 1;
  searchInput: ProductSearchInput = {
    query: "",
  };
  removeItemState: RemoveStateProps | undefined;
  AIQuery: string = "";
  currentObject: SingleItem | null = null;
  hasServerError = false;
  isManufacturerLicence: boolean = false;
  isOpened: boolean = false;
  canCreate = true;
  searchMode: SearchMode | undefined = undefined;

  setFilterOptions(filtersOptions: FiltersOptions) {
    this.filtersOptions = filtersOptions;
  }

  setSelectedFilters(selectedFilters: SelectedItems[]) {
    this.selectedFilters = selectedFilters;
  }

  setSearchResults(searchResults: SearchResults) {
    this.searchResults = searchResults;
  }

  setUsedProducts(usedProducts: SearchResults) {
    this.usedProducts = usedProducts;
  }

  setSuggestedProducts(suggestedProducts: SearchResults) {
    this.suggestedProducts = suggestedProducts;
  }

  setProductSearchLoading(productSearchLoading: boolean) {
    this.productSearchLoading = productSearchLoading;
  }

  setCurrentSearchPage(page: number) {
    this.currentSearchPage = page;
  }

  setRemoveItemState(removeItemState: RemoveStateProps | undefined) {
    this.removeItemState = removeItemState;
  }

  setIsManufacturerLicence(isManufacturerLicence: boolean) {
    this.isManufacturerLicence = isManufacturerLicence;
  }

  setAIQuery(query: string) {
    this.AIQuery = query;
  }

  setHasServerError(hasNetworkError: boolean) {
    this.hasServerError = hasNetworkError;
  }

  setIsOpened(isOpened: boolean) {
    this.isOpened = isOpened;
  }

  setSearchMode(mode: SearchMode | undefined) {
    this.searchMode = mode;
  }

  private hasLoadedAllUsedProducts() {
    return this.usedProducts.count === this.usedProducts.total;
  }

  setCurrentObject(currentObject: SingleItem | null) {
    this.currentObject = currentObject;
  }

  private hasLoadedAllProducts() {
    return this.searchResults.count === this.searchResults.total;
  }

  updateSearchInput<K extends keyof ProductSearchInput>(
    key: K,
    value: ProductSearchInput[K]
  ) {
    this.searchInput = {
      ...this.searchInput,
      [key]: value,
    };

    this.searchProducts();
  }

  resetSearchTable() {
    this.setSearchResults({
      count: 0,
      items: [],
      total: 0,
    });

    this.removeUsedProducts();
    this.removeSuggestions();
  }

  private removeSuggestions() {
    this.setSuggestedProducts({
      count: 0,
      items: [],
      total: 0,
    });
  }

  private removeUsedProducts() {
    this.setUsedProducts({
      count: 0,
      items: [],
      total: 0,
    });
  }

  private createOptionItem(item: SearchEngineCategoryOUT): DropdownOption {
    return {
      id: item.id,
      name: `${item.category_number} ${item.name}`,
      value: item.category_number,
      children: item?.children
        ?.map((rec) => this.createOptionItem(rec))
        .sort((a, b) => a.name.localeCompare(b.name)),
    } as DropdownOption;
  }

  async fetchFilterCategories() {
    // Materials
    const items = await this.fetchMaterials();
    const materials = items?.filter((item) => item.name !== "");

    // Conformities
    await conformityStore.fetchConformities();
    const conformities = conformityStore.conformityOptions
      .filter((item) => item.name !== "")
      .sort((a, b) => a.name.localeCompare(b.name));

    // DIN categories
    const data = await this.fetchDinCategoriesByLevel();
    const dinCategories_1 = data.level_1?.map((item) =>
      this.createOptionItem(item)
    );
    const dinCategories_2 = data.level_2?.map((item) =>
      this.createOptionItem(item)
    );
    const dinCategories_3 = data.level_3?.map((item) =>
      this.createOptionItem(item)
    );

    // Certifications
    await conformityStore.fetchCertificationData();
    const certifications = conformityStore.certificationOptions.filter(
      (item) => item.name !== "Keine"
    );

    this.setFilterOptions({
      ...this.filtersOptions,
      materials: materials ?? [],
      DIN_categories_level_1: dinCategories_1,
      DIN_categories_level_2: dinCategories_2,
      DIN_categories_level_3: dinCategories_3,
      conformities: conformities,
      certifications: certifications,
    });
  }

  async fetchMaterials() {
    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineMaterials()
      .then((response) => response.data);
  }

  async fetchDinCategoriesByLevel() {
    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineProductDinCategoriesByLevel()
      .then((response) => response.data);
  }

  getDinCategoriesNumbers = () => {
    return this.selectedFilters
      .filter((rec) => rec.category.name.includes("DIN"))
      .flatMap((rec) => rec.items)
      .map((item) => item.value)
      .filter((v): v is string => Boolean(v));
  };

  getFilterQueries(
    key: keyof FiltersOptions,
    field_name: keyof DropdownOption = "name"
  ) {
    return this.selectedFilters
      .filter((rec) => rec.category.id === key)
      .flatMap((rec) => rec.items)
      .map((item) => item[field_name]) as string[];
  }

  private getSearchProps(): ProductSearchInput {
    return {
      ...this.searchInput,
      building_id: buildingStore.buildingID,
      organisation_id: sidebarMenuStore.currentOrganisation?.id,
      din_category_numbers: this.getDinCategoriesNumbers(),
      material_name: this.getFilterQueries("materials"),
      epd_conformity: this.getFilterQueries("conformities"),
      certifications: this.getFilterQueries("certifications"),
      publication_status: this.getFilterQueries(
        "publication_status",
        "id"
      ) as PublicationStatus[],
      reference_units: this.getFilterQueries(
        "reference_units",
        "id"
      ) as ReferenceUnitChoices[],
      is_sentinel:
        this.getFilterQueries("other_properties").length > 0 ? true : null,
      mode: this.searchMode,
    };
  }

  async fetchProducts(update?: boolean) {
    const props: ProductSearchInput = this.getSearchProps();

    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineSearchProducts(
        props,
        this.currentSearchPage,
        SEARCH_PAGE_SIZE
      )
      .then((response) => {
        let data: InternalNoEPDSProductSearchOutput[];
        if (update) {
          data = response.data.items;
        } else {
          data = [...this.searchResults.items, ...response.data.items];
        }

        this.setSearchResults({
          count: data.length,
          items: data,
          total: response.data.total_count_without_compliance_filter,
        });
      });
  }

  async fetchUsedProducts(update?: boolean) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;

    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineSearchUsedProducts(
        orgID,
        buildingStore.buildingID,
        this.searchInput.query,
        this.currentSearchPage,
        SEARCH_PAGE_SIZE
      )
      .then((response) => {
        let data: InternalNoEPDSProductSearchOutput[];
        if (update) {
          data = response.data.items;
        } else {
          data = [...this.usedProducts.items, ...response.data.items];
        }

        this.setUsedProducts({
          count: data.length,
          items: data,
          total: response.data.total_count_without_compliance_filter,
        });
      });
  }

  hasAnyFilterSet() {
    return Boolean(
      this.searchInput.material_name?.length ||
        this.selectedFilters?.length ||
        this.searchInput.epd_conformity?.length ||
        this.searchInput.certifications?.length ||
        this.searchInput.is_sentinel ||
        this.searchInput.query
    );
  }

  async fetchAISuggestedProducts() {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;

    const props: ProductSearchInput = this.getSearchProps();

    if (this.hasAnyFilterSet()) {
      this.removeSuggestions();
      return;
    }

    const AIQuery = await this.fetchAIQueryString(this.currentObject?.id);

    if (!AIQuery) {
      this.removeSuggestions();
      return;
    }

    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineSearchProducts(
        { ...props, query: AIQuery, mode: undefined },
        this.currentSearchPage,
        SEARCH_PAGE_SIZE
      )
      .then((response) => {
        const data = response.data.items;

        this.setSuggestedProducts({
          count: data.length,
          items: data.splice(0, 3),
          total: response.data.total_count_without_compliance_filter,
        });
      });
  }

  async searchProducts() {
    if (!sidebarMenuStore.currentOrganisation?.id) return;

    try {
      this.setProductSearchLoading(true);
      this.resetSearchTable();
      this.setCurrentSearchPage(1);

      if (
        !this.searchInput.product_source &&
        !searchStore.usingOrganisationSearch
      ) {
        // Fetch AI Suggestions
        await this.fetchAISuggestedProducts();
        // Fetch used products
        await this.fetchUsedProducts(true);
      } else {
        this.removeSuggestions();
        this.removeUsedProducts();
      }

      // Fetch all products
      if (this.hasLoadedAllUsedProducts() || this.usedProducts.count < 100) {
        await this.fetchProducts(true);
      }

      this.setHasServerError(false);
    } catch (error) {
      this.setHasServerError(true);
    } finally {
      this.setProductSearchLoading(false);
    }
  }

  async fetchNextPage() {
    if (
      this.productSearchLoading ||
      (this.hasLoadedAllUsedProducts() && this.hasLoadedAllProducts())
    )
      return;
    try {
      this.setProductSearchLoading(true);
      this.setCurrentSearchPage(this.currentSearchPage + 1);
      if (!this.hasLoadedAllUsedProducts()) {
        await this.fetchUsedProducts();
      } else {
        await this.fetchProducts();
      }
    } catch (error) {
      console.error("Error fetching search Products: ", error);
    } finally {
      this.setProductSearchLoading(false);
    }
  }

  async fetchAIQueryString(objectID?: string | null) {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID || !objectID) return;
    return await productSearchApi()
      .buildingProductsApiV1RoutersSearchEngineProductSearchAiQuery(
        objectID,
        orgID
      )
      .then((response) => response.data.query_string);
  }

  async changeProductStatus(productID: string, state: "publish" | "unpublish") {
    const orgID = sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    await productsAndComponentsApi()
      .buildingProductsApiV1RoutersProductsPublishProduct(
        productID,
        orgID,
        state
      )
      .then(() => {
        const currentItem = this.searchResults.items.find(
          (rec) => rec.id === productID
        );
        if (currentItem) {
          runInAction(() => {
            currentItem.is_public = state === "publish";
          });
        }
      });
  }

  reset() {
    this.resetSearchTable();
    this.setProductSearchLoading(true);
    this.setSelectedFilters([]);
    this.searchInput = { query: "" };
    this.setIsManufacturerLicence(false);
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: ProductSearchStore;

  static getInstance(): ProductSearchStore {
    if (!ProductSearchStore.instance) {
      ProductSearchStore.instance = new ProductSearchStore();
    }
    return ProductSearchStore.instance;
  }
}
export const productSearchStore = ProductSearchStore.getInstance();
